/**
 * Composable table primitives for the Apple TV section.
 *
 * These deliberately mirror the corporate dashboard's `DataTable.Root / Toolbar /
 * Header / Th / Row / Cell / Empty` so its four signage screens could be ported
 * near line-for-line, keeping the same interactions: clickable rows, inline icon
 * actions on every row, and a tree that indents by nesting rows. Prospera's
 * shared data-driven <DataTable> cannot express those (it renders its own
 * actions menu and owns row composition), which is why this exists.
 *
 * Only the styling is Prospera's — slate palette, ring borders, the same header
 * treatment the rest of the app uses.
 */
import React from 'react';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/lib/utils';

function Root({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'flex min-h-0 flex-1 flex-col rounded-lg border border-slate-200 bg-white p-4 shadow-sm',
        className,
      )}
    >
      {children}
    </div>
  );
}

function Toolbar({ children }: { children: React.ReactNode }) {
  return <div className="mb-4 flex shrink-0 flex-wrap items-center gap-3">{children}</div>;
}

function Content({ children }: { children: React.ReactNode }) {
  return <div className="min-h-0 flex-1 overflow-y-auto rounded-md border border-slate-200">{children}</div>;
}

function Header({ children }: { children: React.ReactNode }) {
  return <thead className="sticky top-0 z-10 bg-slate-50 text-left">{children}</thead>;
}

function Th({ children, className }: { children?: React.ReactNode; className?: string }) {
  return (
    <th
      className={cn(
        'px-3 py-2.5 text-xs font-semibold uppercase tracking-wide text-slate-500',
        className,
      )}
    >
      {children}
    </th>
  );
}

function Row({
  children,
  onClick,
  className,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
}) {
  return (
    <tr
      onClick={onClick}
      className={cn('transition-colors hover:bg-slate-50', onClick && 'cursor-pointer', className)}
    >
      {children}
    </tr>
  );
}

function Cell({
  children,
  truncate,
  actions,
  className,
}: {
  children?: React.ReactNode;
  truncate?: boolean;
  /** Right-aligned action cell; clicks inside never bubble to the row. */
  actions?: boolean;
  className?: string;
}) {
  return (
    <td
      onClick={actions ? (e) => e.stopPropagation() : undefined}
      className={cn(
        'px-3 py-2.5 text-sm text-slate-700',
        truncate && 'max-w-0 truncate',
        actions && 'text-right',
        className,
      )}
    >
      {actions ? <div className="flex items-center justify-end gap-1">{children}</div> : children}
    </td>
  );
}

function Empty({ colSpan, message }: { colSpan: number; message: React.ReactNode }) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-3 py-12 text-center text-sm text-slate-500">
        {message}
      </td>
    </tr>
  );
}

function Loading() {
  return (
    <div className="flex flex-1 items-center justify-center py-16">
      <Spinner label="Loading…" />
    </div>
  );
}

export const Table = { Root, Toolbar, Content, Header, Th, Row, Cell, Empty, Loading };

/**
 * One inline row action — an icon button, the dashboard's TableActions style.
 * Stops propagation so clicking Edit does not also fire the row's own click.
 */
export function IconAction({
  title,
  onClick,
  children,
  tone = 'default',
  disabled,
}: {
  title: string;
  onClick: () => void;
  children: React.ReactNode;
  tone?: 'default' | 'danger' | 'warning' | 'primary';
  disabled?: boolean;
}) {
  const hover = {
    default: 'hover:text-slate-900',
    primary: 'hover:text-primary',
    warning: 'hover:text-amber-600',
    danger: 'hover:text-red-600',
  }[tone];
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={cn(
        'rounded-md p-1.5 text-slate-500 transition-colors hover:bg-slate-100 disabled:opacity-40',
        hover,
      )}
    >
      {children}
    </button>
  );
}
